import axios from "axios";
import dayjs from "dayjs";
import { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const doctorContext = createContext(null);

export const useDoctorContext = () => {
  const context = useContext(doctorContext);
  if (!context) {
    throw new Error(
      "useDoctorContext must be used within a DoctorContextProvider"
    );
  }
  return context;
};
const DoctorContextProvider = ({ children }) => {
  const BackendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("s2stoken");
  const navigate = useNavigate();

  const [totalPatients, setTotalPatients] = useState(0);
  const [filterPatients, setFilterPatients] = useState([]);
  const [followupdateRecords, setFollowupdateRecords] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [doctorsList, setDoctorsList] = useState([]);
  const [sharedPatientsList, setSharedPatientsList] = useState([]);
  const [referredPatientsList, setReferredPatientsList] = useState([]);

  const [vaccines, setVaccines] = useState([]);
  const [vaccineLoading, setVaccineLoading] = useState(false);

  function followUpList(followupdateRecords) {
    const grouped = followupdateRecords.reduce((acc, rec) => {
      const dateObj = new Date(rec.follow_up_date);
      const key = dayjs(dateObj).format("YYYY-MM-DD");
      if (!acc[key]) acc[key] = [];
      acc[key].push(rec);
      return acc;
    }, {});

    const sections = Object.entries(grouped)
      .sort(([a], [b]) => dayjs(a).diff(dayjs(b)))
      .map(([dateKey, records]) => {
        const isToday = dayjs(dateKey).isSame(dayjs(), "day");
        const heading = isToday
          ? "TODAY"
          : dayjs(dateKey).format("DD MMM YYYY");
        return { heading, records };
      });
    setFollowupdateRecords(sections);
  }

  const fetchData = async (currentUser) => {
    try {
      setDashboardLoading(true);
      const docId = currentUser?.pediatrician_id;

      const axiosConfig = {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      };

      // Fetch both APIs in parallel with credentials
      const [patientsRes, followUpRes, referredPatientRes] = await Promise.all([
        axios.get(`${BackendUrl}/api/doctor/${docId}/patients`, axiosConfig),
        axios.get(
          `${BackendUrl}/api/doctor/${docId}/visit-records`,
          axiosConfig
        ),
        axios.get(`${BackendUrl}/api/doctor/${docId}/referred-patients`, axiosConfig)
      ]);

      // Handle patients response
      if (patientsRes?.data?.success) {
        const patients = patientsRes.data?.data;
        // const filteringPatients = patients.filter(
        //   (patient) =>
        //     !patient.created_by_parent ||
        //     (patient.created_by_parent && patient.updated_by_doctor)
        // );
        const sortedPatients = patients.sort((a, b) => {
          return a.first_name
            .toLowerCase()
            .localeCompare(b.first_name.toLowerCase());
        });

        setTotalPatients(patients.length);
        setFilterPatients(sortedPatients);
        console.log("Patients:", patients);
      } else {
        setTotalPatients(0);
        setFilterPatients([]);
      }
      if (followUpRes?.data?.success) {
        followUpList(followUpRes?.data?.data);
      } else {
        setFollowupdateRecords([]);
      }
      if (referredPatientRes?.data?.success) {
        const referredPatients = referredPatientRes.data?.data;
        setReferredPatientsList(referredPatients);
        console.log("Referred Patients:", referredPatients);
      } else {
        setReferredPatientsList([]);
      }

      // Handle follow-up dates response
      console.log("FollowUp Data:", followUpRes?.data);
    } catch (error) {
      if (error?.response?.data?.message === "Token has expired") {
        localStorage.removeItem("s2stoken");
        navigate("/");
      }
      setTotalPatients(0);
      setFollowupdateRecords([]);
      setFilterPatients([]);
      setReferredPatientsList([]);
      toast.error("Failed to get data");
      console.log("Error in fetching data:", error);
    } finally {
      setDashboardLoading(false);
      sessionStorage.setItem("lastFetchTime", Date.now().toString());
    }
  };

  async function fetchVaccinations(docId) {
    try {
      setVaccineLoading(true);
      const response = await axios.get(`${BackendUrl}/api/reminders/${docId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });
      const records = response.data;
      console.log("Upcoming tasks:", records);
      setVaccines(records.patients);
      setVaccineLoading(false);
    } catch (error) {
      setVaccineLoading(false);
      console.error("Error fetching upcoming tasks:", error);
      if (error?.response?.data?.message === "Token has expired") {
        localStorage.removeItem("s2stoken");
        navigate("/");
      }
    }
  }

  const validateDashboard = (currentUser) => {
    const lastFetch = sessionStorage.getItem("lastFetchTime");
    const isDataStale =
      !lastFetch || Date.now() - parseInt(lastFetch, 10) > 6 * 60 * 60 * 1000;

    if (filterPatients.length === 0 || isDataStale) {
      fetchData(currentUser);
    }
  };

  const validateUpcommingTasks = (docId) => {
    const lastVaccineFetch = sessionStorage.getItem("lastVaccineFetchTime");
    const isDataStale =
      !lastVaccineFetch || Date.now() - parseInt(lastVaccineFetch, 10) > 6 * 60 * 60 * 1000;
    if (vaccines.length === 0 || isDataStale) {
      fetchVaccinations(docId);
      sessionStorage.setItem("lastVaccineFetchTime", Date.now().toString());
    }
  }

  // inside your component
  const clearDoctorsStates = () => {
    setTotalPatients(0);
    setFilterPatients([]);
    setFollowupdateRecords([]);
    setDashboardLoading(false);
    setDoctorsList([]);
    setSharedPatientsList([]);
    setVaccines([]);
    setVaccineLoading(false);
    setReferredPatientsList([]);
  };


  return (
    <doctorContext.Provider
      value={{
        totalPatients,
        filterPatients,
        followupdateRecords,
        dashboardLoading,
        validateDashboard,
        fetchData,
        vaccines,
        vaccineLoading,
        validateUpcommingTasks,
        doctorsList,
        setDoctorsList,
        sharedPatientsList,
        setSharedPatientsList,
        clearDoctorsStates,
        referredPatientsList
      }}
    >
      {children}
    </doctorContext.Provider>
  );
};

export default DoctorContextProvider;
